import React from "react";
import ZModal from "@atoms/ZModal";
import ZCreditCardDetails from "./ZCreditCardDetails";
import ZTypography from "@atoms/ZTypography";
import { EFontWeights } from "@atoms/ZTypography/interface";
import CardIllustration from "@/Assets/Illustrations/Card.svg";
import { GlobalState } from "@/Reducers/interface";
import { useSelector } from "react-redux";
import { ZomentumRoutes } from "@/Contracts/common";
import {
	BillingInfo,
	InitializationStep
} from "@/Contracts/Users";
import { memo } from "react";

const ZCreditCardModal = () => {
	const isRouteNotEqualToAuthorizeAndSetup =
		!window.location.href.includes(ZomentumRoutes.Authorize) &&
		!window.location.href.includes(ZomentumRoutes.SetupAccount);

	const {
		showAddCardModal,
		shouldMandateAddCard,
		companyName,
		signUpInitiatingVendor,
		isCPQBuilderUser
	} = useSelector((state: GlobalState) => {
		const isPartnerInvited =
			state.user.configurationSettings.company_initialization_step ===
				InitializationStep.CompanyDetailsCaptured &&
			state.user.configurationSettings.user_initialization_step ===
				InitializationStep.PasswordResetDone &&
			state.user?.billingInfo?.payment_source === null;

		const isGrowPartner = BillingInfo.paymentNeeded(state.user.billingInfo);
		const isCardDetailsNeeded = isPartnerInvited || isGrowPartner;

		return {
			showAddCardModal:
				isRouteNotEqualToAuthorizeAndSetup && isCardDetailsNeeded,
			isLoading: state.user.isBillingInfoLoading,
			companyName: state.user.userCompany.name,
			signUpInitiatingVendor:
				state.user.configurationSettings.signup_initiating_vendor,
			shouldMandateAddCard:
				state.user?.billingInfo?.should_mandate_card_add,
			isCPQBuilderUser:
				!!state.user.userCompany?.signup_extra_info
					?.signup_document_type_selected
		};
	});

	const cardHeading = isCPQBuilderUser
		? "Elevate Your Quoting and Proposal Management Experience"
		: signUpInitiatingVendor
		? "Thank You For Accepting Our Invite!"
		: "Partner With SaaS Vendors to Drive Sales";
	const cardDescription = isCPQBuilderUser
		? "From crafting professional quotes to securing deals and collecting payments, Zomentum provides a complete suite of tools for your success."
		: signUpInitiatingVendor
		? `You can process all ${companyName} invoices on
	the same platform through Zomentum.`
		: "Work with SaaS vendors to sell solutions that align with your clients’ needs—where and when you need them.";

	// If adding a card is not mandatory, do not show the modal.
	const showCardModal = !shouldMandateAddCard ? false : showAddCardModal;

	return (
		<ZModal
			visible={showCardModal}
			title="Add a Card"
			centered
			size="lg"
			footer={null}
			closable={false}
			className="z-credit-card-modal"
		>
			<div className="flex flex-row">
				<div className="p-4 border border-solid border-gray-5 rounded flex-1">
					<ZCreditCardDetails />
				</div>
				<div className="flex flex-col items-center justify-between pl-15 flex-1">
					<div className="flex flex-col">
						<ZTypography.ZText
							colorVariant={9}
							weight={EFontWeights.MEDIUM}
						>
							{cardHeading}
						</ZTypography.ZText>
						<ZTypography.ZText className="whitespace-normal">
							{cardDescription}
						</ZTypography.ZText>
						{signUpInitiatingVendor ? (
							<ZTypography.ZText
								colorVariant={8}
								className="mt-2"
							>
								Don't worry, we are not taking any payment at
								this point. We store the card to ensure smoother
								transactions with your vendors. You will be
								notified before we charge your card.
							</ZTypography.ZText>
						) : null}
					</div>
					<div className="flex justify-end w-full relative -bottom-4">
						<div className="relative -right-6">
							<CardIllustration />
						</div>
					</div>
				</div>
			</div>
		</ZModal>
	);
};

export default memo(ZCreditCardModal);
